import { getCLS, getFID, getFCP, getLCP, getTTFB, Metric } from 'web-vitals';
import { WebVitalMetric } from '../types/analytics';

// Enhanced Web Vitals integration with Google Analytics
// This module sends Core Web Vitals data to GA4 for performance monitoring

// Send metric to Google Analytics
function sendToGoogleAnalytics(metric: Metric) {
  if (typeof (window as any).gtag !== 'function') {
    // If GA is not loaded, store metrics for later
    if (!window.webVitalsBuffer) {
      window.webVitalsBuffer = [];
    }
    window.webVitalsBuffer.push(metric);
    return;
  }

  // Send to GA4 as a custom event
  (window as any).gtag('event', 'web_vitals', {
    event_category: 'Web Vitals',
    event_label: metric.name,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    custom_parameter_1: metric.id,
    metric_name: metric.name,
    metric_value: metric.value,
    metric_delta: metric.delta,
    metric_rating: getMetricRating(metric.name, metric.value),
    page_path: window.location.pathname,
    connection_type: getConnectionType(),
    device_type: getDeviceType()
  });

  // Also send as a custom metric for more detailed analysis
  (window as any).gtag('event', `vitals_${metric.name.toLowerCase()}`, {
    event_category: 'Performance',
    event_label: `${metric.name}: ${metric.value}`,
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    metric_rating: getMetricRating(metric.name, metric.value)
  });

  if (process.env.NODE_ENV === 'development') {
    console.log('Web Vital sent to GA:', metric.name, metric.value, getMetricRating(metric.name, metric.value));
  }
}

// Get performance rating based on Core Web Vitals thresholds
function getMetricRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = {
    CLS: [0.1, 0.25],
    FID: [100, 300],
    FCP: [1800, 3000],
    LCP: [2500, 4000],
    TTFB: [800, 1800]
  };

  const [good, poor] = thresholds[name as keyof typeof thresholds] || [0, 0];
  
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

// Get connection type information
function getConnectionType(): string {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  
  if (connection) {
    return connection.effectiveType || connection.type || 'unknown';
  }
  
  return 'unknown';
}

// Get device type based on screen size and user agent
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent;
  
  if (width < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return width < 768 ? 'mobile' : 'tablet';
  }
  
  return 'desktop';
}

// Initialize Web Vitals tracking
export function initWebVitals() {
  // Track all Core Web Vitals
  getCLS(sendToGoogleAnalytics);
  getFID(sendToGoogleAnalytics);
  getFCP(sendToGoogleAnalytics);
  getLCP(sendToGoogleAnalytics);
  getTTFB(sendToGoogleAnalytics);

  // Send buffered metrics when GA loads
  const checkAndSendBuffered = () => {
    if (typeof (window as any).gtag === 'function' && window.webVitalsBuffer) {
      window.webVitalsBuffer.forEach((metric: WebVitalMetric) => {
        sendToGoogleAnalytics(metric);
      });
      window.webVitalsBuffer = [];
    }
  };

  // Check periodically if GA has loaded
  const intervalId = setInterval(() => {
    if (typeof (window as any).gtag === 'function') {
      checkAndSendBuffered();
      clearInterval(intervalId);
    }
  }, 100);

  // Clear interval after 10 seconds to avoid infinite checking
  setTimeout(() => clearInterval(intervalId), 10000);

  if (process.env.NODE_ENV === 'development') {
    console.log('Web Vitals tracking initialized');
  }
}

// Send custom performance metrics
export function sendCustomMetric(name: string, value: number, category: string = 'Custom Performance') {
  if (typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', 'custom_metric', {
      event_category: category,
      event_label: name,
      value: Math.round(value),
      metric_name: name,
      metric_value: value
    });
  }
}

// Track page load performance
export function trackPageLoadPerformance() {
  if ('performance' in window) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        if (navigation) {
          // Track key timing metrics
        const metrics = {
          dns_lookup: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcp_connection: navigation.connectEnd - navigation.connectStart,
          server_response: navigation.responseEnd - navigation.requestStart,
          dom_content_loaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
          page_load_complete: navigation.loadEventEnd - navigation.fetchStart
        };

          Object.entries(metrics).forEach(([name, value]) => {
            if (value > 0) {
              sendCustomMetric(name, value, 'Page Load Timing');
            }
          });
        }

        // Track resource loading performance
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        const resourceStats = {
          total_resources: resources.length,
          css_resources: resources.filter(r => r.name.includes('.css')).length,
          js_resources: resources.filter(r => r.name.includes('.js')).length,
          image_resources: resources.filter(r => /\.(jpg|jpeg|png|gif|webp|svg)/.test(r.name)).length
        };

        Object.entries(resourceStats).forEach(([name, value]) => {
          sendCustomMetric(name, value, 'Resource Loading');
        });

      }, 0);
    });
  }
}

// Global types are now imported from types/analytics.ts

export default initWebVitals;
